import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
	DialogHandler,
	Button,
	Spinner,
} from "cakemail-ui-components/lib/react";
import { Translate, I18n } from "react-redux-i18n";
import { connect } from "react-redux";
import {
	createCampaignLogExportService,
	createCampaignsReportsExport,
	createContactExportService, createSuppressedEmailsExport,
	downloadCampaignLogExportService, downloadCampaignReportsExportService,
	downloadContactExportService, downloadSuppressedEmailsExportService,
	getContactExportService,
	getTaskService,
} from "assets/js/services/taskService";
import { Link, withRouter } from "react-router-dom";
import { isOrganization, isPartner } from "utils/apiUtils";
import { EXPORT_TYPES } from "assets/js/const/Exports";

import fetchRoute from "utils/routes";
import { limitsFeaturesAccess, LIMIT_FEATURE } from "assets/js/utils/LimitsAndFeatures";

let retry = 0;

const CreateExportDialog = (props) => {
	const [cExport, setExport] = useState({});
	const spinner = useRef(null);
	const callInterval = useRef(null);
	const downloadRef = useRef(null);

	useEffect(() => {
		doExport();
		spinner.current.setProgression(100);
	}, []);

	function getExport(exportId) {
		getTaskService(
			{ exportId: exportId },
			(data) => {
				setExport(data.data);
				if (["ready", "failed", "deleted"].includes(data.data.status)) {
					clearInterval(callInterval.current);
				}

				if (spinner.current) {
					spinner.current.setProgression(100 - data.data.progress);
					if (data.data.progress === 100) {
						spinner.current.drawCheck();
					}
				}
			},
			() => {
				retry += 1;
				if (retry > 2) {
					clearInterval(callInterval.current);
				}
			}
		);
	}

	function doExport() {
		if (isPartner(true, { hideDialog: true }) && !limitsFeaturesAccess(LIMIT_FEATURE.USE_EMAIL_API)) {
			return;
		}

		if (props.exportType === EXPORT_TYPES.CONTACTS) {
			let options = { id: props.selectedList?.id };
			if (props.jsonQuery) options.json = props.jsonQuery;

			if (props.segment) {
				options.description =
					props.segment.name + `;;${props.selectedList.name}`;
			} else {
				options.description = `${props.selectedList.name}`;
			}
			createContactExportService(
				options,
				setStartingInterval,
				showGeneralExportError
			);
		} else if (props.exportType === EXPORT_TYPES.CAMPAIGN_REPORTS) {
			createCampaignsReportsExport(
				{},
				setStartingInterval,
				showGeneralExportError
			);
		} else if (props.exportType === EXPORT_TYPES.SUPPRESSED_EMAILS) {
			createSuppressedEmailsExport(
				null,
				setStartingInterval,
				showGeneralExportError
			);
		} else {
			createCampaignLogExportService(
				{
					campaignId: props.campaign.id,
					description: props.campaign.name,
				},
				setStartingInterval,
				showGeneralExportError
			);
		}
	}

	function setStartingInterval(data) {
		setExport(data.data);
		callInterval.current = setInterval(() => {
			getExport(data.data.id);
		}, 5000);
	}

	function showGeneralExportError(msg = null, error, code) {
		//2051 to many suppressed emails to export
		if (code !== 2051) {
			DialogHandler.hide();
			DialogHandler.show({
				type: "error",
				title: <Translate value={msg ? msg : "app-general-export-error"} />,
			});
		}
	}

	function download() {
		function success(data) {
			window.open(data.data.url, "_blank");
		}

		function failure(msg) {
			DialogHandler.show({
				type: "error",
				title: (
					<Translate
						value={
							msg ? msg : "app-general-error-generic-problems_no_retry"
						}
					/>
				),
			});
		}


		if (props.exportType === EXPORT_TYPES.CONTACTS) {
			downloadContactExportService(
				{ exportId: cExport.id, listId: props.selectedList.id },
				success,
				failure
			);
		} else if (props.exportType === EXPORT_TYPES.CAMPAIGN_REPORTS) {
			downloadCampaignReportsExportService(
				{ exportId: cExport.id },
				success,
				failure
			);
		} else if (props.exportType === EXPORT_TYPES.SUPPRESSED_EMAILS) {
			downloadSuppressedEmailsExportService(
				{ exportId: cExport.id },
				success,
				failure
			);
		} else {
			downloadCampaignLogExportService(
				{ exportId: cExport.id, campaignId: props.campaign.id },
				success,
				failure
			);
		}
	}

	function close() {
		clearInterval(callInterval.current);
		DialogHandler.hide();
	}

	let title = "app-contacts-export-dialog-exporting-title";
	let desc = <Translate value="app-contacts-export-dialog-content-pending" />;

	if ([EXPORT_TYPES.CAMPAIGN_LOG, EXPORT_TYPES.CAMPAIGN_REPORTS].includes(props.exportType)) {
		title = "app-campaign-activity-export-dialog-exporting-title";
	} else if (props.exportType === EXPORT_TYPES.SUPPRESSED_EMAILS) {
		title = "app-suppressed-emails-export-dialog-title";
	}

	if (cExport?.status) {
		if (["ready", "failed", "deleted"].includes(cExport.status)) {
			if ([EXPORT_TYPES.CAMPAIGN_LOG, EXPORT_TYPES.CAMPAIGN_REPORTS].includes(props.exportType)) {
				title = "app-campaign-activity-export-dialog-title";
			} else if (props.exportType === EXPORT_TYPES.SUPPRESSED_EMAILS) {
				title = "app-suppressed-emails-export-dialog-title-done";
			} else {
				title = "app-contacts-export-dialog-title";
			}

			// Because Dialog setup is outside hasrouter, links dont work properly and must come from hash components
			// Or find another way
			desc = (
				<>
					<Translate value="app-contacts-export-dialog-content-1" />{" "}
					<a
						href={"#" + fetchRoute("accountExports")}
						onClick={(e) => {
							DialogHandler.hide();
						}}
					>
						<Translate value="app-account-submenu-account-activity" />
					</a>{" "}
					<Translate value="app-contacts-export-dialog-content-2" />
				</>
			);
		} else if (cExport.progress > 0) {
			//Exporting
			if (props.exportType === EXPORT_TYPES.CAMPAIGN_LOG) {
				desc = (
					<Translate value="app-campaign-activity-export-dialog-content-exporting" />
				);
			} else {
				desc = (
					<Translate value="app-contacts-export-dialog-content-exporting" />
				);
			}
		}
	}

	title = <Translate value={title} />;

	const manualSpinner = !!(cExport?.progress && cExport?.progress > 0);
	return (
		<div className={`exportDialog ${props.exportType}`}>
			<div className="titleContent">
				<Spinner
					key={manualSpinner ? "manual" : "auto"}
					ref={spinner}
					isManualInput={manualSpinner}
					radius={12}
					strokeWidth={2}
					checkWidth={4}
				/>
				<p className="default hasBtnRow lead">{title}</p>
			</div>

			<div className="dialogContent">
				<p>{desc}</p>
			</div>

			<div className="dialogBtns">
				{cExport?.status === "ready" ? (
					<>
						<Button
							shapeStyle="stroke"
							colorTheme="grey"
							size="small"
							radius="square"
							text={<Translate value="app-general-done" />}
							onClick={close}
						/>
						<Button
							ref={downloadRef}
							shapeStyle="fill"
							colorTheme="teal"
							size="small"
							radius="square"
							text={<Translate value="app-general-download" />}
							onClick={download}
						/>
					</>
				) : (
					<>
						<Button
							shapeStyle="stroke"
							colorTheme="grey"
							size="small"
							radius="square"
							text={<Translate value="app-general-cancel" />}
							onClick={close}
							className="hidden"
						/>
						<Button
							shapeStyle="fill"
							colorTheme="teal"
							size="small"
							radius="square"
							text={<Translate value="app-general-got-it" />}
							onClick={close}
						/>
					</>
				)}
			</div>
		</div>
	);
};

CreateExportDialog.propTypes = {
	selectedList: PropTypes.object,
	selectedContacts: PropTypes.array,
	segment: PropTypes.object,
	query: PropTypes.string,
	campaign: PropTypes.object,
	history: PropTypes.object,
	exportType: PropTypes.oneOf(Object.values(EXPORT_TYPES)),
	jsonQuery: PropTypes.object,
};

CreateExportDialog.defaultProps = {};

export default withRouter(
	connect((state) => {
		return {};
	}, null)(CreateExportDialog)
);
